import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { orchestratorReducer, initialOrchestratorState } from './reducer';
const OrchestratorContext = /*#__PURE__*/createContext(null);
function useOrchestratorFlow(flow, onError, loadingComponent, initialIndex, onStepChange) {
  const [state, dispatch] = useReducer(orchestratorReducer, {
    ...initialOrchestratorState,
    currentIndex: initialIndex ?? 0
  });
  const lastExecutedRef = useRef(null);
  const getCurrentItem = useCallback(() => {
    return flow[state.currentIndex];
  }, [flow, state.currentIndex]);
  const getCurrentStep = useCallback(() => {
    let index = state.currentIndex;
    while (index >= 0) {
      const item = flow[index];
      if ((item == null ? void 0 : item.type) === 'step') {
        return {
          id: item.id,
          component: item.component,
          params: item.params
        };
      }
      index--;
    }
    return null;
  }, [flow, state.currentIndex]);
  const canGoBack = useCallback(() => {
    if (state.isLoading) {
      return false;
    }
    let effectiveIndex = state.currentIndex;
    while (effectiveIndex >= 0) {
      var _flow$effectiveIndex;
      if (((_flow$effectiveIndex = flow[effectiveIndex]) == null ? void 0 : _flow$effectiveIndex.type) === 'step') {
        break;
      }
      effectiveIndex--;
    }
    if (effectiveIndex <= 0) {
      return false;
    }
    for (let i = effectiveIndex - 1; i >= 0; i--) {
      var _flow$i;
      if (((_flow$i = flow[i]) == null ? void 0 : _flow$i.type) === 'step') {
        return true;
      }
    }
    return false;
  }, [flow, state.currentIndex, state.isLoading]);
  const getTotalSteps = useCallback(() => {
    return flow.filter(item => item.type === 'step').length;
  }, [flow]);
  const canGoNext = useCallback(() => {
    if (state.isLoading) {
      return false;
    }
    return state.currentIndex < flow.length - 1;
  }, [flow.length, state.currentIndex, state.isLoading]);
  const isComplete = useCallback(() => {
    if (state.isLoading) {
      return false;
    }
    return state.currentIndex >= flow.length - 1;
  }, [flow.length, state.currentIndex, state.isLoading]);
  const getCurrentStepIndex = useCallback(() => {
    let effectiveIndex = state.currentIndex;
    while (effectiveIndex >= 0) {
      var _flow$effectiveIndex2;
      if (((_flow$effectiveIndex2 = flow[effectiveIndex]) == null ? void 0 : _flow$effectiveIndex2.type) === 'step') {
        break;
      }
      effectiveIndex--;
    }
    if (effectiveIndex < 0) return 0;
    let stepIndex = 0;
    for (let i = 0; i < effectiveIndex; i++) {
      var _flow$i2;
      if (((_flow$i2 = flow[i]) == null ? void 0 : _flow$i2.type) === 'step') {
        stepIndex++;
      }
    }
    return stepIndex;
  }, [flow, state.currentIndex]);
  const findPreviousStepIndex = useCallback(() => {
    for (let i = state.currentIndex - 1; i >= 0; i--) {
      var _flow$i3;
      if (((_flow$i3 = flow[i]) == null ? void 0 : _flow$i3.type) === 'step') {
        return i;
      }
    }
    return 0;
  }, [flow, state.currentIndex]);
  const executeCurrentItem = useCallback(async () => {
    const currentItem = getCurrentItem();
    if (!currentItem) {
      return;
    }
    const signature = `${state.currentIndex}:${state.navigationContext.skipped}`;
    if (lastExecutedRef.current === signature && (currentItem.type === 'action' || currentItem.type === 'step' || currentItem.type === 'task')) {
      return;
    }
    lastExecutedRef.current = signature;
    try {
      switch (currentItem.type) {
        case 'step':
          break;
        case 'task':
          currentItem.handler(state.navigationContext);
          dispatch({
            type: 'NEXT'
          });
          break;
        case 'action':
          dispatch({
            type: 'SET_LOADING',
            payload: true
          });
          await currentItem.handler(state.navigationContext);
          dispatch({
            type: 'SET_LOADING',
            payload: false
          });
          dispatch({
            type: 'NEXT'
          });
          break;
      }
    } catch (error) {
      if (currentItem.type === 'action') {
        dispatch({
          type: 'SET_LOADING',
          payload: false
        });
      }
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (onError) {
        onError(errorMessage, currentItem.id);
      }
      if (currentItem.type === 'action' || currentItem.type === 'task') {
        const previousStepIndex = findPreviousStepIndex();
        dispatch({
          type: 'GO_TO_INDEX',
          payload: previousStepIndex
        });
      }
    }
  }, [getCurrentItem, onError, findPreviousStepIndex, state.navigationContext, state.currentIndex]);
  useEffect(() => {
    executeCurrentItem();
  }, [executeCurrentItem]);
  const next = useCallback(async () => {
    if (state.isLoading) {
      return;
    }
    if (state.currentIndex >= flow.length - 1) {
      return;
    }
    dispatch({
      type: 'NEXT'
    });
  }, [flow.length, state.currentIndex, state.isLoading]);
  const skip = useCallback(async () => {
    if (state.isLoading) {
      return;
    }
    if (state.currentIndex >= flow.length - 1) {
      return;
    }
    dispatch({
      type: 'SKIP'
    });
  }, [flow.length, state.currentIndex, state.isLoading]);
  const back = useCallback(() => {
    if (state.isLoading) {
      return;
    }
    if (!canGoBack()) {
      return;
    }
    for (let i = state.currentIndex - 1; i >= 0; i--) {
      var _flow$i4;
      if (((_flow$i4 = flow[i]) == null ? void 0 : _flow$i4.type) === 'step') {
        dispatch({
          type: 'GO_TO_INDEX',
          payload: i
        });
        break;
      }
    }
  }, [canGoBack, flow, state.currentIndex, state.isLoading]);
  const reset = useCallback(() => {
    dispatch({
      type: 'RESET'
    });
  }, []);
  const currentStep = getCurrentStep();
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep);
    }
  }, [currentStep, onStepChange]);
  return useMemo(() => ({
    currentStep,
    currentParams: currentStep == null ? void 0 : currentStep.params,
    isLoading: state.isLoading,
    currentStepIndex: getCurrentStepIndex(),
    totalSteps: getTotalSteps(),
    isComplete: isComplete(),
    canGoBack: canGoBack(),
    canGoNext: canGoNext(),
    loadingComponent,
    next,
    skip,
    back,
    reset
  }), [currentStep, state.isLoading, getCurrentStepIndex, getTotalSteps, isComplete, canGoBack, canGoNext, loadingComponent, next, skip, back, reset]);
}
export function OrchestratorProvider(_ref) {
  let {
    flow,
    children,
    loadingComponent,
    onError,
    initialIndex,
    onStepChange
  } = _ref;
  const orchestrator = useOrchestratorFlow(flow, onError, loadingComponent, initialIndex, onStepChange);
  return /*#__PURE__*/React.createElement(OrchestratorContext.Provider, {
    value: orchestrator
  }, children);
}
export function useOrchestrator() {
  const context = useContext(OrchestratorContext);
  if (!context) {
    throw new Error('useOrchestrator must be used within an OrchestratorProvider');
  }
  return context;
}
//# sourceMappingURL=context.js.map